import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

const API_URL = window.location.origin;

const INTEREST_OPTIONS = [
  { value: 'retreats', label: 'Retreats' },
  { value: 'community', label: 'Sister Diamonds Community' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'all', label: 'All of it' },
];

export const PriorityListSection = () => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', interest: 'retreats' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.email.trim()) {
      toast.error('Please enter your first name and email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/retreat/interest`, {
        full_name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        how_heard: 'waitlist-next-retreat',
        interest: form.interest,
      });
      setDone(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="priority-list" className="py-24 px-6 bg-forest text-white" data-testid="priority-list-section">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-4xl md:text-5xl font-bold mb-10 leading-tight">
          Maybe You&apos;ve Been the Strong One <span className="text-terracotta">Long Enough.</span>
        </h2>

        <div className="space-y-1 text-lg md:text-xl text-white/85 leading-relaxed mb-8">
          <p>You have carried the work.</p>
          <p>The family.</p>
          <p>The vision.</p>
          <p>The expectations.</p>
          <p>The decisions.</p>
          <p>The people who count on you.</p>
        </div>

        <div className="space-y-1 text-lg text-white/85 leading-relaxed mb-12">
          <p>There are moments when the next move is not another thing to accomplish.</p>
          <p>Sometimes the next move is giving yourself permission to stop.</p>
          <p className="font-heading text-xl text-terracotta italic mt-4">
            To receive. To reconnect. To Just Be.
          </p>
        </div>

        {done ? (
          <div className="bg-white/10 rounded-2xl p-10 backdrop-blur-sm" data-testid="priority-list-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-terracotta flex items-center justify-center mb-4">
              <Check className="h-7 w-7 text-white" />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-2">You&apos;re on the priority list!</h3>
            <p className="text-white/80">
              You&apos;ll be among the first to know when the next Just Be™ experience is announced. Until then — just be.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-8 md:p-10 text-left shadow-2xl">
            <h3 className="font-heading text-2xl font-bold text-forest text-center mb-6 uppercase tracking-wide">
              Be First to Know About the Next Retreat
            </h3>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pl-first" className="text-forest">First Name *</Label>
                  <Input id="pl-first" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="First name" className="mt-1" data-testid="priority-first-name-input" />
                </div>
                <div>
                  <Label htmlFor="pl-last" className="text-forest">Last Name</Label>
                  <Input id="pl-last" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Last name" className="mt-1" data-testid="priority-last-name-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="pl-email" className="text-forest">Email *</Label>
                <Input id="pl-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="mt-1" data-testid="priority-email-input" />
              </div>
              <div>
                <Label htmlFor="pl-phone" className="text-forest">Mobile Number</Label>
                <Input id="pl-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-5555" className="mt-1" data-testid="priority-phone-input" />
              </div>
              <div>
                <Label className="text-forest">Which experience interests you?</Label>
                <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                  <SelectTrigger className="mt-1" data-testid="priority-interest-select">
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTEREST_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`interest-option-${o.value}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-terracotta hover:bg-terracotta/90 text-white h-12 text-lg" data-testid="priority-list-submit-btn">
                {submitting ? 'Adding you...' : 'Join the Priority List'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};
